<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import KanbanCard from './KanbanCard.vue';

const props = defineProps({
  stage: { type: Object, required: true },
  cards: { type: Array, default: () => [] },
  funnelId: { type: [Number, String], required: true },
});

const emit = defineEmits([
  'addCard',
  'editCard',
  'deleteCard',
  'dropCard',
  'wonCard',
  'lostCard',
]);

const { t } = useI18n();

const totalValue = computed(() => {
  const sum = props.cards.reduce((acc, c) => acc + Number(c.value || 0), 0);
  if (sum === 0) return null;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(sum);
});

const headerStyle = computed(() => ({
  borderBottom: `2px solid ${props.stage.color}`,
  backgroundColor: `${props.stage.color}30`,
}));

const onDragOver = e => {
  e.preventDefault();
  e.currentTarget.classList.add('ring-2', 'ring-n-brand/30');
};

const onDragLeave = e => {
  e.currentTarget.classList.remove('ring-2', 'ring-n-brand/30');
};

const onDrop = e => {
  e.preventDefault();
  e.currentTarget.classList.remove('ring-2', 'ring-n-brand/30');
  const cardId = Number(e.dataTransfer.getData('cardId'));
  if (cardId) {
    emit('dropCard', {
      cardId,
      stageId: props.stage.id,
      position: props.cards.length,
    });
  }
};
</script>

<template>
  <div
    class="flex flex-col flex-shrink-0 w-[300px] rounded-xl shadow-sm transition-shadow"
    @dragover="onDragOver"
    @dragleave="onDragLeave"
    @drop="onDrop"
  >
    <!-- Header with stage color accent -->
    <div
      class="flex items-center justify-between gap-2 px-3 py-2.5 rounded-t-xl"
      :style="headerStyle"
    >
      <div class="flex items-center gap-2 min-w-0">
        <span
          class="size-2.5 rounded-full flex-shrink-0"
          :style="{ backgroundColor: stage.color }"
        />
        <span class="text-sm font-semibold text-n-slate-12 truncate">
          {{ stage.name }}
        </span>
        <span
          class="bg-n-alpha-2 px-1.5 py-0.5 rounded-full text-[11px] font-medium text-n-slate-10 flex-shrink-0"
        >
          {{ cards.length }}
        </span>
      </div>
      <div class="flex items-center gap-1.5">
        <span
          v-if="totalValue"
          class="text-[11px] font-medium text-green-600 dark:text-green-400"
        >
          {{ totalValue }}
        </span>
        <button
          class="size-7 grid place-content-center rounded-lg hover:bg-n-alpha-2 transition-colors"
          @click="emit('addCard', stage)"
        >
          <span class="i-lucide-plus size-4 text-n-slate-11" />
        </button>
      </div>
    </div>

    <!-- Card list area -->
    <div
      class="flex flex-col gap-2 p-2 overflow-y-auto flex-1 min-h-[100px] bg-n-slate-2 dark:bg-n-slate-3 rounded-b-xl"
    >
      <KanbanCard
        v-for="card in cards"
        :key="card.id"
        :card="card"
        :funnel-id="funnelId"
        @edit="emit('editCard', card)"
        @delete="emit('deleteCard', card)"
        @won="emit('wonCard', card)"
        @lost="emit('lostCard', card)"
      />

      <!-- Empty state -->
      <div
        v-if="cards.length === 0"
        class="flex flex-col items-center justify-center py-6 gap-2"
      >
        <span class="i-lucide-inbox size-8 text-n-slate-6" />
        <span class="text-[11px] text-n-slate-9">
          {{ t('CRM.EMPTY_STAGE') }}
        </span>
      </div>
    </div>
  </div>
</template>
